<template>
  <div class="community-page">
    <div class="community-inner">
      <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
      <header class="community-hero">
        <h1>Meet your fellow IABF-ers</h1>
        <p class="lede">
          Say hello to other IABF-ers —
          <button type="button" class="inline-link-btn" @click="openIntroModal">
            {{ hasMyIntro ? 'edit your intro' : 'introduce yourself' }}
          </button>.
        </p>
        <button
          type="button"
          class="iabf-btn iabf-btn--primary hero-cta"
          @click="openIntroModal"
        >
          {{ hasMyIntro ? 'Edit my intro' : 'Introduce yourself' }}
        </button>
      </header>

      <div class="view-toggle" role="tablist" aria-label="Community view">
        <button
          type="button"
          role="tab"
          class="view-toggle-btn"
          :class="{ active: viewMode === 'cards' }"
          :aria-selected="viewMode === 'cards' ? 'true' : 'false'"
          @click="viewMode = 'cards'"
        >
          Cards
        </button>
        <button
          type="button"
          role="tab"
          class="view-toggle-btn"
          :class="{ active: viewMode === 'map' }"
          :aria-selected="viewMode === 'map' ? 'true' : 'false'"
          @click="viewMode = 'map'"
        >
          Map
        </button>
      </div>

      <div v-if="loading" class="loading-state">Loading intros…</div>

      <div v-else-if="!intros.length" class="empty-state iabf-card">
        <p>No introductions yet — be the first!</p>
        <button type="button" class="iabf-btn iabf-btn--primary" @click="openIntroModal">
          Introduce yourself →
        </button>
      </div>

      <community-map
        v-else-if="viewMode === 'map'"
        :intros="intros"
      />

      <div v-else class="intro-grid">
        <article
          v-for="card in intros"
          :key="card.id"
          class="intro-card iabf-card"
          :class="{ mine: card.id === myDancerId, 'intro-card--artie': isArtieCard(card) }"
        >
          <div class="intro-card-top">
            <button
              v-if="isArtieCard(card)"
              type="button"
              class="intro-avatar intro-avatar--artie intro-avatar--zoom"
              aria-label="See Artie larger"
              @click="artieLightbox = true"
            >
              <img :src="card.headshotUrl" :alt="card.displayName || 'Artie'">
            </button>
            <div v-else class="intro-avatar" :class="{ empty: !card.headshotUrl }">
              <img v-if="card.headshotUrl" :src="card.headshotUrl" :alt="card.displayName || 'Dancer'">
              <span v-else>{{ initials(card) }}</span>
            </div>
            <div class="intro-card-id">
              <h2 class="intro-name">
                {{ card.displayName || nameFrom(card) }}
                <span v-if="isArtieCard(card)" class="faculty-badge">Mascot</span>
                <span v-else-if="isFacultyCard(card)" class="faculty-badge">Faculty</span>
              </h2>
              <p v-if="card.comingFrom" class="intro-from">{{ card.comingFrom }}</p>
            </div>
          </div>
          <dl class="intro-facts">
            <div v-if="card.yearsDancing && !isFacultyCard(card)">
              <dt>Dancing</dt>
              <dd>{{ card.yearsDancing }}</dd>
            </div>
            <div v-if="yearsAtIabfLabel(card)">
              <dt>IABF</dt>
              <dd>{{ yearsAtIabfLabel(card) }}</dd>
            </div>
            <div v-if="stayLabel(card) && !isFacultyCard(card)">
              <dt>Stay</dt>
              <dd>{{ stayLabel(card) }}</dd>
            </div>
            <div v-if="feelingsFor(card).length">
              <dt>Feeling</dt>
              <dd class="feeling-pills">
                <span
                  v-for="f in feelingsFor(card)"
                  :key="f"
                  class="feeling-pill"
                >{{ f }}</span>
              </dd>
            </div>
          </dl>
          <p v-if="noteFor(card)" class="intro-note">“{{ noteFor(card) }}”</p>
          <div v-if="card.id === myDancerId" class="intro-card-actions">
            <button
              type="button"
              class="edit-intro-link"
              @click="openIntroModal"
            >
              Edit
            </button>
          </div>
        </article>
      </div>

      <p v-if="loadError" class="load-error" role="alert">{{ loadError }}</p>
    </div>

    <intro-edit-modal
      v-model="introModalOpen"
      @saved="onIntroSaved"
    />
    <div
      v-if="artieLightbox"
      class="artie-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Artie the Armadillo"
      @click.self="artieLightbox = false"
      @keydown.esc.prevent="artieLightbox = false"
    >
      <button type="button" class="artie-lightbox-close" aria-label="Close" @click="artieLightbox = false">×</button>
      <img src="/brand/artie-armadillo.jpg" alt="Artie the Armadillo — IABF mascot">
      <p class="artie-lightbox-caption">Look closely — the shell is full of IABF history.</p>
    </div>
  </div>
</template>

<script>
import { saveErrorMessage } from '@/utils/save-error'
import { isFacultyAccount } from '@/utils/iabf-faculty'
import {
  ARTIE_COMMUNITY_INTRO,
  isArtieIntro,
  introIsPublishable,
  introStayLabel,
  introYearsAtIabfLabel,
  normalizeIntroFeelings
} from '@/config/iabf-intro'
import IntroEditModal from '@/components/iabf/IntroEditModal.vue'
import CommunityMap from '@/components/iabf/CommunityMap.vue'

export default {
  name: 'IABFCommunity',
  components: { IntroEditModal, CommunityMap },
  data () {
    return {
      loading: true,
      loadError: '',
      intros: [],
      introModalOpen: false,
      viewMode: 'cards',
      artieLightbox: false
    }
  },
  computed: {
    myDancerId () {
      const d = this.$store.getters.currentIABFDancer
      return d && d.id ? d.id : null
    },
    hasMyIntro () {
      if (!this.myDancerId) return false
      return this.intros.some(i => i.id === this.myDancerId)
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    const start = this.$store.state.masqueradeDancerId
      ? this.$store.dispatch('refreshEffectiveDancer').catch(() => {})
      : Promise.resolve()
    start.then(() => this.load()).then(() => {
      if (this.$route.query && (this.$route.query.edit === '1' || this.$route.query.edit === 'true')) {
        this.openIntroModal()
      }
    })
  },
  methods: {
    openIntroModal () {
      this.introModalOpen = true
    },
    async onIntroSaved () {
      await this.load()
    },
    async load () {
      this.loading = true
      this.loadError = ''
      try {
        const intros = await this.$store.dispatch('loadDancerIntros')
        const people = (intros || []).filter(i => introIsPublishable(i) && !isArtieIntro(i))
        this.intros = people
        const me = this.$store.getters.currentIABFDancer
        if (me && me.id && !this.intros.some(i => i.id === me.id)) {
          const mine = await this.$store.dispatch('loadMyDancerIntro', { dancerId: me.id })
          if (mine && introIsPublishable(mine)) {
            this.intros = [mine, ...this.intros]
          }
        }
      } catch (e) {
        this.loadError = saveErrorMessage(e, 'Could not load the community feed.')
      }
      this.intros = [ARTIE_COMMUNITY_INTRO, ...this.intros.filter(i => !isArtieIntro(i))]
      this.loading = false
    },
    feelingsFor (card) {
      return normalizeIntroFeelings(card && card.feeling)
    },
    noteFor (card) {
      return String((card && card.note) || '').trim()
    },
    stayLabel (card) {
      return introStayLabel(card)
    },
    yearsAtIabfLabel (card) {
      return introYearsAtIabfLabel(card)
    },
    isArtieCard (card) {
      return isArtieIntro(card)
    },
    isFacultyCard (card) {
      if (isArtieIntro(card)) {
        return false
      }
      if (isFacultyAccount(card)) {
        return true
      }
      const me = this.$store.getters.currentIABFDancer
      return !!(card && me && card.id === me.id && isFacultyAccount(me))
    },
    nameFrom (card) {
      return `${card.firstName || ''} ${card.lastName || ''}`.trim() || 'Dancer'
    },
    initials (card) {
      const a = (card.firstName || card.displayName || '?').charAt(0)
      const b = (card.lastName || '').charAt(0)
      return (a + b).toUpperCase()
    }
  }
}
</script>

<style scoped>
.community-page {
  min-height: 100%;
  padding: 24px 16px 64px;
  background: var(--iabf-bg, #f6f4f1);
}
.community-inner {
  max-width: 880px;
  margin: 0 auto;
}
.view-toggle {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  margin: 0 0 20px;
  background: rgba(28, 27, 31, 0.05);
  border-radius: 999px;
}
.view-toggle-btn {
  border: none;
  background: transparent;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  color: var(--iabf-ink-muted, #5c5a63);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.view-toggle-btn.active {
  background: #fff;
  color: var(--iabf-ink, #1c1b1f);
  box-shadow: 0 1px 3px rgba(28, 27, 31, 0.1);
}
.view-toggle-btn:hover:not(.active) {
  color: var(--iabf-ink, #1c1b1f);
}
.back-link {
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
}
.back-link:hover { color: var(--iabf-ink, #1c1b1f); }
.community-hero h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 34px;
  letter-spacing: -.02em;
  margin: 12px 0 8px;
  color: var(--iabf-ink, #1c1b1f);
}
.lede {
  font-size: 16px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 14px;
}
.inline-link-btn {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
.hero-cta {
  margin: 0 0 24px;
}
.iabf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  margin-top: 0;
}
.iabf-btn--primary {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}
.loading-state, .empty-state {
  padding: 28px 20px;
  text-align: center;
  color: var(--iabf-ink-muted, #5c5a63);
}
.empty-state .iabf-btn {
  margin-top: 12px;
  display: inline-flex;
  text-decoration: none;
}
.intro-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}
.intro-card {
  padding: 16px 16px 14px;
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.intro-card.mine {
  border-color: rgba(138, 149, 125, 0.55);
}
.intro-card--artie {
  background: var(--iabf-bg-soft, #eef0eb);
  border-color: rgba(138, 149, 125, 0.35);
}
.intro-avatar--artie img {
  object-fit: contain;
  mix-blend-mode: multiply;
}
.intro-avatar--zoom {
  padding: 0;
  border: none;
  cursor: zoom-in;
}
.artie-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(28, 27, 31, 0.88);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 20px 40px;
}
.artie-lightbox img {
  max-width: min(720px, 100%);
  max-height: calc(100vh - 100px);
  object-fit: contain;
  background: #fff;
  border-radius: 16px;
}
.artie-lightbox-caption {
  margin: 14px 0 0;
  color: #fff;
  font-size: 15px;
  text-align: center;
  max-width: 36em;
}
.artie-lightbox-close {
  position: absolute;
  top: 12px;
  right: 16px;
  border: none;
  background: none;
  color: #fff;
  font-size: 36px;
  line-height: 1;
  cursor: pointer;
}
.intro-card-top {
  display: flex;
  gap: 12px;
  align-items: center;
}
.intro-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--iabf-bg-soft, #eef0eb);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  font-size: 18px;
}
.intro-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.intro-name {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 20px;
  margin: 0 0 2px;
  color: var(--iabf-ink, #1c1b1f);
}
.faculty-badge {
  display: inline-block;
  margin-left: 8px;
  vertical-align: middle;
  font-family: var(--iabf-font-body, inherit);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  padding: 3px 8px;
  border-radius: 999px;
  white-space: nowrap;
}
.intro-from {
  margin: 0;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.intro-facts {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.intro-facts dt {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
}
.intro-facts dd {
  margin: 2px 0 0;
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
}
.feeling-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.feeling-pill {
  display: inline-block;
  background: var(--iabf-bg-soft, #eef0eb);
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}
.intro-note {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  font-style: italic;
  color: var(--iabf-ink-muted, #5c5a63);
}
.intro-card-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 4px;
}
.edit-intro-link {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  text-decoration: underline;
}
.edit-intro-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}
.load-error {
  color: var(--iabf-danger, #b54a4a);
  margin-top: 16px;
}
</style>
